import { Button } from '../../components/ui';
import { ProductionPoDocument } from './ProductionPoDocument';
import { poFromSnapshot } from './productionPo';
import { ProofImage } from './ProofImage';
import { ArtworkDownload } from './ArtworkDownload';
import type { PoDocument, PoSnapshot } from './productionPo';

/**
 * A PO as it was issued, printable.
 *
 * Renders a FROZEN snapshot from po_documents — never the order's current
 * values. That distinction is the whole reason the table exists: a PO printed
 * from live data silently changes when the order does, so a factory can be
 * holding a sheet that matches nothing, with no record it ever differed.
 *
 * ── One layout ────────────────────────────────────────────────────────────
 *
 * This used to render its own bare table. That meant TAP printed a 4ocean
 * letterhead PO with payment status and notes to the manufacturer, while the
 * factory downloaded a plain sheet with none of it — two documents, two field
 * sets, and the thinner one was the legal record. Both now go through
 * ProductionPoDocument, so the sheet the factory reads is the sheet TAP sends.
 *
 * The frozen extras that have no place on a blank PO — the client's approval
 * certificate and the approved artwork — are passed as children and land above
 * the footer.
 *
 * ── The watermark ─────────────────────────────────────────────────────────
 *
 * A superseded revision prints with SUPERSEDED across it. Without that, two
 * printed sheets from the same job are indistinguishable on a workbench —
 * which is the failure a revision system is supposed to prevent, arriving as
 * paper instead of as data.
 *
 * No money, ever. The snapshot carries none: issue_po_revision does not read a
 * price, cost or total column, and productionPo.test.ts asserts the rendered
 * markup contains no price, cost, margin, total, usd or revenue.
 */

export type { PoDocument, PoSnapshot };

/**
 * How a reference file is named on the sheet.
 *
 * The client logo's kind says what it is. The two extra slots do not, so they
 * carry a label the uploader typed and that label is what the factory reads —
 * falling back to the kind only for a row written before labels existed.
 */
export function referenceName(file: { kind: string; label?: string }): string {
  if (file.kind === 'client_logo') return 'Client logo';
  const label = (file.label ?? '').trim();
  return label === '' ? file.kind : label;
}

/**
 * `onClose` is optional since the in-force revision started rendering OPEN on
 * the job page (TAP's and the factory's): an inline document has nowhere to
 * close to, so the button only appears when a caller passes somewhere to go.
 */
export function PoDocumentView({ doc, onClose }: { doc: PoDocument; onClose?: () => void }) {
  const snap = doc.snapshot;
  const superseded = doc.superseded_at !== null;
  const po = poFromSnapshot(doc);

  function print() {
    document.body.classList.add('po-printing');
    window.addEventListener('afterprint', () => document.body.classList.remove('po-printing'), {
      once: true,
    });
    window.print();
  }

  return (
    // po-print-root is what the print stylesheet isolates. Without it,
    // printing hides #root and its descendants and silently produces a BLANK
    // PAGE — display:none on an ancestor cannot be undone further down.
    <div className="po-doc-wrap po-print-root">
      <div className="po-doc-actions po-no-print">
        {onClose === undefined ? null : (
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        )}
        <Button size="sm" onClick={print}>
          Download / Print
        </Button>
      </div>

      <ProductionPoDocument
        po={po}
        superseded={superseded}
        notice={
          superseded
            ? `This revision is no longer in force${
                doc.reason === '' ? '' : ` — ${doc.reason}`
              }. A later revision has been issued.`
            : ''
        }
      >
        {snap.certificate == null ? null : (
          <section className="po-section">
            <h2 className="po-section-label">CLIENT APPROVAL</h2>
            <p className="po-notes">
              Approved {snap.certificate.signed_at.slice(0, 10)} · certificate{' '}
              {snap.certificate.certificate_no}
            </p>
          </section>
        )}

        {(snap.proofs ?? []).length === 0 ? null : (
          <section className="po-section">
            <h2 className="po-section-label">APPROVED ARTWORK</h2>
            {/* Frozen alongside the terms. A PO pointing at artwork that has
                since been revised is the same failure as one that regenerates
                its own quantities. */}
            <ul className="po-doc-proofs">
              {(snap.proofs ?? []).map((proof) => (
                <li key={`${proof.component}-${String(proof.version)}`}>
                  <ProofImage url={proof.url} alt={proof.component} />
                  <span>
                    {proof.component} v{proof.version}
                  </span>
                  {/* The frozen URL, so the factory downloads the file this
                      revision was issued with rather than whatever is live on
                      the order today. */}
                  <ArtworkDownload url={proof.url} component={proof.component} />
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* The client's logo, frozen with the artwork. Its own heading rather
            than a fifth item under APPROVED ARTWORK, because it is not
            approved artwork: nobody signed it off, and a factory reading the
            sheet should not think somebody did. */}
        {(snap.reference_files ?? []).length === 0 ? null : (
          <section className="po-section">
            <h2 className="po-section-label">REFERENCE FILES</h2>
            <ul className="po-doc-proofs">
              {(snap.reference_files ?? []).map((file) => (
                <li key={`${file.kind}-${String(file.version)}`}>
                  <ProofImage url={file.url} alt={referenceName(file)} />
                  <span>
                    {referenceName(file)} v{file.version}
                  </span>
                  <ArtworkDownload url={file.url} component={referenceName(file)} />
                </li>
              ))}
            </ul>
          </section>
        )}
      </ProductionPoDocument>
    </div>
  );
}
